import { AnalysisCriteriaAlert } from '@/features/analyses/components';
import { useDownloadBusinessQuotePdf } from '@/features/quotes/hooks/business/use-download-business-quote-pdf.hook';
import { BusinessLocationsResume } from '@/features/proposals/components/proposal-steps/business-locations-resume.component';
import { PremiumCard } from '@/features/proposals/components/proposal-steps/premium-card.component';
import {
  ResumeGroup,
  ResumeItem,
} from '@/features/proposals/components/proposal-steps/step-resume.component';
import { toaster } from '@/shared/ui/toaster.component';
import { formatCurrency } from '@/shared/utils';
import { formatPhone } from '@/shared/utils/format-phone';
import { formatRegisterNumber } from '@/shared/utils/format-register-number';
import { downloadFromSignedUrl } from '@/shared/utils/signed-url.util';
import { Button } from '@noorden-insurtech/alba-design-system';
import { Download } from 'lucide-react';
import { useCallback } from 'react';
import type { ProposalAnalysisDetail } from '../../types';

interface Step1ResumeProps {
  detail: ProposalAnalysisDetail;
  underwritingMessages: string[];
  onGoToDecision: () => void;
  onExit: () => void;
}

export function Step1Resume({
  detail,
  underwritingMessages,
  onGoToDecision,
  onExit,
}: Step1ResumeProps) {
  const totalValue = formatCurrency(detail.premium.total);
  const liquidValue = formatCurrency(detail.premium.net);
  const iofValue = formatCurrency(detail.premium.iof);

  const phone = (() => {
    const formatted = formatPhone(detail.proponente.telefone);
    return formatted === 'N/A' ? '-' : formatted;
  })();

  const policyId = detail.id;
  const productCode = detail.proposal.productCode ?? '';
  const downloadQuotePdf = useDownloadBusinessQuotePdf();

  const handleDownloadQuotePdf = useCallback(async () => {
    if (!productCode) {
      toaster.create({
        type: 'error',
        title: 'Erro ao baixar PDF da cotação',
        description: 'Código do produto não encontrado.',
      });
      return;
    }

    try {
      const { url, key } = await downloadQuotePdf.mutateAsync({
        policyId,
        productCode,
      });
      const filename = key.split('/').pop() || `cotacao-${policyId}.pdf`;
      await downloadFromSignedUrl(url, filename);
    } catch (error) {
      toaster.create({
        type: 'error',
        title: 'Erro ao baixar PDF da cotação',
        description:
          error instanceof Error
            ? error.message
            : 'Não foi possível baixar o PDF. Tente novamente.',
      });
    }
  }, [downloadQuotePdf, policyId, productCode]);

  return (
    <div className='flex flex-col gap-6'>
      <AnalysisCriteriaAlert messages={underwritingMessages} />

      <div className='flex flex-col gap-8 bg-white shadow-xl rounded-2xl py-12 px-8'>
        <PremiumCard
          totalValue={totalValue}
          liquidValue={liquidValue}
          interestPercentage='0,00%'
          interestValue='R$ 0,00'
          iofValue={iofValue}
          product={detail.product}
          proposalNumber={detail.proposalNumber}
          status='Pendente Análise'
          showPisCofins={false}
          defaultExpanded
        />

        <div className='flex flex-col gap-6'>
          <p className='font-montserrat text-base font-normal text-alba-black-500'>
            Informações da Proposta
          </p>

          <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
            <ResumeGroup title='Dados do Proponente'>
              <ResumeItem
                label='CPF'
                value={formatRegisterNumber(detail.proponente.cpf)}
              />
              <ResumeItem label='Nome' value={detail.proponente.nome} />
              <ResumeItem label='E-mail' value={detail.proponente.email} />
              <ResumeItem label='Telefone' value={phone} />
            </ResumeGroup>
          </div>
        </div>

        <div className='flex flex-row gap-6'>
          <div className='flex flex-col gap-2'>
            <p className='font-montserrat text-sm font-bold text-alba-black-500'>
              PDF da Cotação
            </p>
            <Button
              variant='secondary'
              size='md'
              onClick={() => void handleDownloadQuotePdf()}
              disabled={downloadQuotePdf.isPending}
              loading={downloadQuotePdf.isPending}
            >
              <Download className='w-4 h-4' />
              Ver PDF
            </Button>
          </div>
        </div>

        <div className='flex flex-col gap-6'>
          <p className='font-montserrat text-base font-normal text-alba-black-500'>
            Informações dos Riscos
          </p>

          <BusinessLocationsResume proposal={detail.proposal} />
        </div>
      </div>

      <div className='flex flex-row gap-4 justify-between'>
        <Button variant='ghost' size='md' onClick={onExit}>
          Sair
        </Button>
        <Button variant='primary' size='md' onClick={onGoToDecision}>
          Ir para Decisão
        </Button>
      </div>
    </div>
  );
}
